import { tr, usePartnerTranslation } from "../lib/language";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Store,
  User,
  UserCog,
  Heart,
  Flag,
  ScrollText,
  Users,
  Smile,
  Info,
  ArrowLeft,
  Eye,
  EyeOff,
  AlertCircle,
  Loader2,
  Check,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { parseApiError } from "../lib/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth, defaultPageFor } from "../lib/auth";
import {
  BUSINESS_TYPES,
  EMAIL_REGEX,
  validatePassword,
} from "../lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const ORG_FIELD_MAX = 50;

const ICONS = {
  store: Store,
  "user-star": UserCog,
  user: User,
  heart: Heart,
  flag: Flag,
  scroll: ScrollText,
  users: Users,
  smile: Smile,
};

/* --------------------------- STEP 1 --------------------------- */
const BusinessTypeStep = ({ selected, setSelected, onNext, businessTypes = BUSINESS_TYPES }) => {
  usePartnerTranslation();
  return (
    <section
      data-testid="signup-step-1"
      className="mx-auto w-full max-w-[1100px] px-4 py-10 md:py-16"
    >
      <div className="text-center">
        <p className="text-sm font-medium text-[#6B7280]" data-testid="step-indicator">{tr("Step 1 of 2")}</p>
        <div className="mt-2 flex items-center justify-center gap-2">
          <h1
            data-testid="step-1-heading"
            className="text-2xl font-bold text-[#0F1115] sm:text-3xl md:text-[28px]"
          >{tr("How do you run your business?")}</h1>
          <TooltipProvider delayDuration={120}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={tr("What is this?")}
                  data-testid="step-1-info-icon"
                  className="mt-0.5 text-[#6B7280] transition-colors hover:text-[#0F1115]"
                >
                  <Info className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                className="max-w-xs text-xs leading-relaxed"
              >{tr("Select the option that best describes your business structure. This helps us tailor onboarding for your organisation.")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div
        role="radiogroup"
        aria-label={tr("Business type")}
        className="mx-auto mt-10 grid w-full gap-4 md:grid-cols-2 md:gap-5"
      >
        {businessTypes.map((bt) => {
          const Icon = ICONS[bt.icon] || User;
          const isActive = selected === bt.id;
          return (
            <button
              key={bt.id}
              type="button"
              role="radio"
              aria-checked={isActive}
              data-testid={`business-type-${bt.id}`}
              onClick={() => setSelected(bt.id)}
              className={[
                "group relative flex items-start gap-4 rounded-xl border p-5 text-start transition-all duration-200",
                "hover:-translate-y-0.5 hover:shadow-[0_10px_24px_-12px_rgba(15,17,21,0.18)]",
                isActive
                  ? "border-[#FF5757] bg-[#FF5757] text-white shadow-[0_14px_30px_-14px_rgba(255,87,87,0.55)]"
                  : "border-[#E6E8EC] bg-white text-[#0F1115] hover:border-[#CFD2D8]",
              ].join(" ")}
            >
              <span
                className={[
                  "mt-0.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg transition-colors",
                  isActive ? "bg-white/15" : "bg-[#F5F6F8]",
                ].join(" ")}
              >
                <Icon
                  className={`h-5 w-5 ${isActive ? "text-white" : "text-[#0F1115]"}`}
                  strokeWidth={1.6}
                />
              </span>
              <span className="flex-1">
                <span className="block text-[15px] font-bold leading-tight sm:text-base">
                  {bt.label}
                </span>
                <span
                  className={`mt-1 block text-xs leading-snug sm:text-[13px] ${isActive ? "text-white/90" : "text-[#6B7280]"}`}
                >
                  {bt.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="mx-auto mt-10 flex max-w-md flex-col items-center gap-4">
        <button
          data-testid="signup-next-btn"
          onClick={onNext}
          disabled={!selected}
          className={[
            "inline-flex h-12 w-full items-center justify-center rounded-lg px-6 text-sm font-semibold tracking-wide transition-all",
            selected
              ? "humrahe-btn-primary"
              : "cursor-not-allowed bg-[#EEF0F3] text-[#9BA1AB]",
          ].join(" ")}
        >{tr("Next")}</button>
        <p className="text-sm text-[#0F1115]">{tr("Already have an account?")}{" "}
          <Link
            to="/"
            data-testid="signup-signin-link"
            className="font-semibold text-[#0F1115] underline underline-offset-4 hover:text-[#2476FF]"
          >{tr("Sign in")}</Link>
        </p>
      </div>
    </section>
  );
};

/* --------------------------- STEP 2 --------------------------- */
const FIELD_KEYS = [
  "companyName",
  "shopHandle",
  "website",
  "yourName",
  "email",
  "password",
];

const SHOP_HANDLE_RE = /^[A-Za-z0-9_-]{3,30}$/;

const formatShopHandle = (value = "") => {
  const withoutAt = value.replace(/^@+/, "");
  const handle = withoutAt
    .toLowerCase()
    .trim()
    .replace(/&/g, " and ")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30);
  return `@${handle}`;
};

const shopHandleValue = (value = "") => value.replace(/^@+/, "").trim();

const validateShopHandle = (value = "") => {
  const handle = shopHandleValue(value);
  if (!handle) return tr("Voocly shop handle is required.");
  if (handle.length < 3) return tr("Handle must be at least 3 characters.");
  if (handle.length > 30) return tr("Handle can't be longer than 30 characters.");
  if (!SHOP_HANDLE_RE.test(handle))
    return tr("Only letters, numbers, '-' and '_' are allowed.");
  return "";
};

const useShopHandleAvailability = (value) => {
  const [state, setState] = useState({ status: "idle" });
  const handle = shopHandleValue(value);
  const validationError = validateShopHandle(value);

  useEffect(() => {
    if (validationError) {
      setState({ status: "idle" });
      return undefined;
    }

    setState({ status: "checking" });
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/shops/check-handle`, {
          params: { handle },
        });
        if (res.data?.available) {
          setState({ status: "available" });
        } else {
          setState({
            status: "taken",
            reason: res.data?.reason || "This shop handle is already taken.",
            suggestion: res.data?.suggestion,
          });
        }
      } catch {
        setState({ status: "error", reason: "Couldn't check handle availability." });
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [handle, validationError]);

  return state;
};

const BusinessDetailsStep = ({ businessType, onBack }) => {
  usePartnerTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [values, setValues] = useState({
    companyName: "",
    shopHandle: "@",
    website: "",
    currency: "USD",
    yourName: "",
    email: "",
    password: "",
  });
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [created, setCreated] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const shopHandleAvailability = useShopHandleAvailability(values.shopHandle);
  const shopHandleForUrl = shopHandleValue(values.shopHandle);
  const shopHandleHelper =
    shopHandleAvailability.status === "checking"
      ? tr("Checking handle availability...")
      : shopHandleAvailability.status === "available"
        ? `Available. Your shop URL: www.${shopHandleForUrl}.voocly.com`
        : shopHandleAvailability.status === "taken" || shopHandleAvailability.status === "error"
          ? ""
          : `Your shop URL will be: www.${shopHandleForUrl || "your-shop-handle"}.voocly.com`;

  const errors = useMemo(() => {
    const e = {};
    if (!values.companyName.trim())
      e.companyName = tr("Company / Brand / Public Name is required.");
    else if (values.companyName.trim().length > ORG_FIELD_MAX)
      e.companyName = tr("Company / Brand / Public Name can't be longer than {{value0}} characters.", { value0: ORG_FIELD_MAX });
    const handleError = validateShopHandle(values.shopHandle);
    if (handleError) e.shopHandle = handleError;
    else if (shopHandleAvailability.status === "taken")
      e.shopHandle = shopHandleAvailability.reason || "This shop handle is already taken.";
    else if (shopHandleAvailability.status === "error")
      e.shopHandle = shopHandleAvailability.reason || "Couldn't check handle availability.";
    if (!values.yourName.trim()) e.yourName = tr("Your name is required.");
    if (!values.email) e.email = tr("Email is required.");
    else if (!EMAIL_REGEX.test(values.email.trim()))
      e.email = tr("Please enter a valid email address.");
    const pwErr = validatePassword(values.password);
    if (pwErr) e.password = pwErr;
    return e;
  }, [values, shopHandleAvailability]);

  const formValid =
    Object.keys(errors).length === 0 &&
    emailVerified;
  const allValid =
    formValid &&
    shopHandleAvailability.status !== "checking";

  const showError = (key) => touched[key] && errors[key];

  const handleChange = (key) => (val) => {
    setValues((v) => ({ ...v, [key]: val }));
  };

  const handleCompanyNameChange = (val) => {
    const next = val.slice(0, ORG_FIELD_MAX);
    setValues((v) => ({
      ...v,
      companyName: next,
      shopHandle: formatShopHandle(next),
    }));
  };

  const handleShopHandleChange = (val) => {
    setValues((v) => ({ ...v, shopHandle: formatShopHandle(val) }));
  };

  const handleBlur = (key) => () =>
    setTouched((t) => ({ ...t, [key]: true }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(FIELD_KEYS.reduce((acc, k) => ({ ...acc, [k]: true }), {}));
    setServerError("");
    if (!allValid) return;

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/signup`, {
        business_type: businessType,
        company_name: values.companyName.trim(),
        shop_handle: shopHandleValue(values.shopHandle),
        website: values.website.trim() || null,
        currency: values.currency,
        your_name: values.yourName.trim(),
        email: values.email.trim(),
        password: values.password,
      });
      setCreated(true);
      const apiUser = res.data?.user;
      if (apiUser) {
        login({
          user: {
            email: apiUser.email,
            full_name: apiUser.full_name || apiUser.your_name,
            role: apiUser.role || "administration",
            is_owner: apiUser.is_owner !== false,
          },
        });
        toast.success(tr("Welcome to Voocly"), {
          description: tr("You're signed in, {{value0}}.", { value0: apiUser.your_name || values.yourName }),
        });
        // Brief delay so the success step animation reads, then navigate.
        setTimeout(() => {
          navigate(defaultPageFor(apiUser.role || "administration"), {
            replace: true,
          });
        }, 800);
      } else {
        // Fallback (older backend without auto-login token) — show success
        // card with manual sign-in link.
        toast.success(tr("Account created"), {
          description: tr("Welcome, {{value0}}!", { value0: apiUser?.your_name || values.yourName }),
        });
      }
    } catch (err) {
      const { code, message } = parseApiError(err, tr("Could not create your account. Please try again."));
      const msg = code === "conflict"
        ? message.toLowerCase().includes("email")
          ? "An account with this email already exists. Please sign in instead."
          : message
        : code === "invalid_passcode"
          ? "Invalid passcode"
        : message;
      setServerError(msg);
      toast.error(tr("Sign up failed"), { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="signup-step-2"
      className="mx-auto w-full max-w-[760px] px-4 py-10 md:py-16"
    >
      <div className="text-center">
        <p className="text-sm font-medium text-[#6B7280]" data-testid="step-indicator">{tr("Step 2 of 2")}</p>
        <h1
          data-testid="step-2-heading"
          className="mt-1 text-2xl font-bold text-[#0F1115] sm:text-[28px]"
        >{tr("Tell us a bit more about your business")}</h1>
      </div>

      <form
        noValidate
        onSubmit={handleSubmit}
        data-testid="signup-form"
        className="humrahe-card mx-auto mt-8 w-full px-5 py-8 sm:px-10 sm:py-12"
      >
        <button
          type="button"
          onClick={onBack}
          data-testid="signup-back-btn"
          className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#6B7280] transition-colors hover:text-[#0F1115]"
        >
          <ArrowLeft className="h-4 w-4" />{tr("Back")}</button>

        <div className="space-y-5">
          <TextField
            id="companyName"
            label={tr("Company / Brand / Public Name")}
            required
            placeholder=""
            value={values.companyName}
            onChange={(e) => handleCompanyNameChange(e.target.value)}
            onBlur={handleBlur("companyName")}
            error={showError("companyName") ? errors.companyName : ""}
            maxLength={ORG_FIELD_MAX}
            testid="field-companyName"
          />

          <TextField
            id="shopHandle"
            label={tr("Voocly shop handle")}
            placeholder={tr("@your-shop")}
            value={values.shopHandle}
            onChange={(e) => handleShopHandleChange(e.target.value)}
            onBlur={handleBlur("shopHandle")}
            required
            error={
              (showError("shopHandle") ||
                shopHandleAvailability.status === "taken" ||
                shopHandleAvailability.status === "error")
                ? errors.shopHandle
                : ""
            }
            helperText={shopHandleHelper}
            testid="field-shopHandle"
          />
          {shopHandleAvailability.status === "taken" &&
            shopHandleAvailability.suggestion && (
              <button
                type="button"
                onClick={() =>
                  handleShopHandleChange(`@${shopHandleAvailability.suggestion}`)
                }
                data-testid="field-shopHandle-suggestion"
                className="-mt-3 inline-flex text-xs font-semibold text-[#FF5757] hover:underline"
              >{tr("Use @")}{shopHandleAvailability.suggestion}
              </button>
            )}

          <TextField
            id="website"
            label={tr("Website")}
            placeholder="www.abc-company.com"
            value={values.website}
            onChange={(e) => handleChange("website")(e.target.value)}
            onBlur={handleBlur("website")}
            testid="field-website"
          />

          <div className="rounded-lg border border-[#EAECEF] bg-[#F8FAFC] px-4 py-3 text-sm text-[#4B5563]" data-testid="field-currency">{tr("Payout currency: ")}<span className="font-semibold text-[#0F1115]">USD</span>
          </div>

          <TextField
            id="yourName"
            label={tr("Your name")}
            required
            value={values.yourName}
            onChange={(e) => handleChange("yourName")(e.target.value)}
            onBlur={handleBlur("yourName")}
            error={showError("yourName") ? errors.yourName : ""}
            testid="field-yourName"
          />

          <EmailOtpField
            email={values.email}
            onEmailChange={(v) => {
              handleChange("email")(v);
              if (emailVerified) setEmailVerified(false);
            }}
            onBlur={handleBlur("email")}
            verified={emailVerified}
            setVerified={setEmailVerified}
            error={showError("email") ? errors.email : ""}
          />

          <PasswordField
            id="password"
            label={tr("Password")}
            required
            value={values.password}
            onChange={(e) => handleChange("password")(e.target.value)}
            onBlur={handleBlur("password")}
            visible={showPassword}
            onToggle={() => setShowPassword((s) => !s)}
            error={showError("password") ? errors.password : ""}
            testid="field-password"
            helperText={
              validatePassword(values.password)
                ? "At least 8 characters and include at least 1 number."
                : ""
            }
          />
        </div>

        {serverError && (
          <div
            data-testid="signup-server-error"
            className="mt-5 flex items-start gap-2 rounded-lg border border-[#FDD8D8] bg-[#FFF4F4] px-3 py-2.5 text-sm text-[#B42318]"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{serverError}</span>
          </div>
        )}

        {created && (
          <div
            data-testid="signup-success"
            className="mt-5 flex items-start gap-2 rounded-lg border border-[#C6E8D0] bg-[#F0FAF3] px-3 py-2.5 text-sm text-[#1F7A3D]"
          >
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{tr("Your partner account has been created. Hang tight — the dashboard will be wired up next.")}</span>
          </div>
        )}

        <div className="mt-8 flex flex-col items-center gap-4">
          <button
            type="submit"
            data-testid="create-account-btn"
            disabled={submitting || !allValid}
            className={[
              "inline-flex h-12 w-full max-w-sm items-center justify-center rounded-lg px-6 text-sm font-semibold tracking-wide transition-all",
              allValid && !submitting
                ? "humrahe-btn-primary"
                : "cursor-not-allowed bg-[#EEF0F3] text-[#9BA1AB]",
            ].join(" ")}
          >
            {submitting ? (
              <>
                <Loader2 className="me-2 h-4 w-4 animate-spin" />{tr("Creating…")}</>
            ) : (
              tr("Create account")
            )}
          </button>
          <p className="text-sm text-[#0F1115]">{tr("Already have an account?")}{" "}
            <Link
              to="/"
              data-testid="signup2-signin-link"
              className="font-semibold text-[#0F1115] underline underline-offset-4 hover:text-[#2476FF]"
            >{tr("Sign in")}</Link>
          </p>
          <p
            className="text-center text-xs text-[#6B7280]"
            data-testid="policies-disclaimer"
          >{tr("By creating an account, you agree to our")}{" "}
            <Link
              to="/partner-legal-center"
              className="text-[#FF5757] underline underline-offset-2 hover:text-[#e14a4a]"
            >{tr("policies")}</Link>
            .
          </p>
        </div>
      </form>
    </section>
  );
};

/* ---------------------- Email + OTP field ---------------------- */
const EmailOtpField = ({ email, onEmailChange, onBlur, verified, setVerified, error }) => {
  usePartnerTranslation();
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  const emailValid = EMAIL_REGEX.test((email || "").trim());

  React.useEffect(() => {
    if (cooldown > 0) {
      const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [cooldown]);

  const sendOtp = async () => {
    setSending(true);
    setOtpError("");
    try {
      const res = await axios.post(`${API}/auth/send-otp`, {
        email: email.trim(),
      });
      setOtpSent(true);
      setCooldown(60);
      const devOtp = res.data?.dev_otp;
      toast.success(tr("OTP sent"), {
        description: devOtp
          ? tr("DEV OTP: {{value0}} — expires in 10 minutes. (No mailer wired yet)", { value0: devOtp })
          : "Check your email for the 6-digit code.",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Could not send OTP. Please try again.";
      toast.error(tr("Failed to send OTP"), { description: msg });
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async () => {
    if (otpValue.length !== 6) {
      setOtpError(tr("Enter the 6-digit code."));
      return;
    }
    setVerifying(true);
    setOtpError("");
    try {
      await axios.post(`${API}/auth/verify-otp`, {
        email: email.trim(),
        otp: otpValue,
      });
      setVerified(true);
      setOtpSent(false);
      setOtpValue("");
      toast.success(tr("Email verified"));
    } catch (err) {
      const { code, message } = parseApiError(err, tr("Invalid passcode"));
      const msg =
        code === "invalid_passcode" || code === "unauthenticated"
          ? "Invalid passcode"
          : message || "Invalid passcode";
      setOtpError(msg);
    } finally {
      setVerifying(false);
    }
  };

  const handleChangeEmail = () => {
    setVerified(false);
    setOtpSent(false);
    setOtpValue("");
    setOtpError("");
  };

  const rightPad = verified
    ? "!pe-32"
    : emailValid && !otpSent
      ? "!pe-28"
      : "";

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <label htmlFor="email" className="humrahe-field-label">{tr("Your email")}<span className="text-[#E5484D]">*</span>
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          onBlur={onBlur}
          disabled={verified}
          data-testid="field-email"
          aria-invalid={!!error}
          className={`humrahe-field ${error ? "humrahe-field-error" : ""} ${rightPad} ${verified ? "bg-[#F7FBF8] text-[#1F7A3D]" : ""}`}
        />
        <div className="absolute end-2 top-1/2 flex -translate-y-1/2 items-center gap-2">
          {verified ? (
            <>
              <span
                data-testid="email-verified-badge"
                className="inline-flex items-center gap-1 rounded-full bg-[#EAF7EE] px-2.5 py-1 text-[11px] font-semibold text-[#1F7A3D]"
              >
                <Check className="h-3.5 w-3.5" strokeWidth={3} />{tr("Verified")}</span>
              <button
                type="button"
                onClick={handleChangeEmail}
                data-testid="email-change-btn"
                className="text-[11px] font-semibold text-[#2476FF] transition-colors hover:text-[#1861dc]"
              >{tr("Change")}</button>
            </>
          ) : emailValid && !otpSent ? (
            <button
              type="button"
              onClick={sendOtp}
              disabled={sending}
              data-testid="send-otp-btn"
              className="inline-flex items-center gap-1 rounded-md bg-[#EEF4FF] px-3 py-1.5 text-xs font-semibold text-[#2476FF] transition-colors hover:bg-[#DCE8FF] disabled:opacity-60"
            >
              {sending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : null}
              {sending ? tr("Sending") : tr("Send OTP")}
            </button>
          ) : null}
        </div>
      </div>

      {error && (
        <p
          data-testid="field-email-error"
          className="flex items-center gap-1.5 ps-1 text-xs font-medium text-[#E5484D]"
        >
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}

      {otpSent && !verified && (
        <div
          data-testid="otp-section"
          className="mt-3 rounded-xl border border-[#E6E8EC] bg-[#FBFBFC] p-4"
        >
          <p className="text-xs text-[#6B7280]">{tr("Enter the 6-digit code sent to")}{" "}
            <span className="font-semibold text-[#0F1115]">{email}</span>
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              value={otpValue}
              onChange={(e) =>
                setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              inputMode="numeric"
              maxLength={6}
              placeholder="••••••"
              data-testid="otp-input"
              className="humrahe-field !h-11 flex-1 !pe-4 tracking-[0.55em]"
            />
            <button
              type="button"
              onClick={verifyOtp}
              disabled={verifying || otpValue.length !== 6}
              data-testid="otp-verify-btn"
              className="humrahe-btn-primary inline-flex h-11 items-center justify-center rounded-lg px-5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            >
              {verifying ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />{tr("Verifying")}</>
              ) : (
                tr("Verify")
              )}
            </button>
          </div>
          {otpError && (
            <p
              data-testid="otp-error"
              className="mt-2 flex items-center gap-1.5 text-xs font-medium text-[#E5484D]"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              {otpError}
            </p>
          )}
          <div className="mt-3 flex items-center justify-between text-xs">
            <span className="text-[#6B7280]">{tr("Didn't get it?")}</span>
            <button
              type="button"
              onClick={sendOtp}
              disabled={cooldown > 0 || sending}
              data-testid="otp-resend-btn"
              className="font-semibold text-[#2476FF] transition-colors hover:text-[#1861dc] disabled:text-[#B6BAC2]"
            >
              {cooldown > 0 ? tr("Resend in {{value0}}s", { value0: cooldown }) : tr("Resend OTP")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------------- Reusable field parts ---------------------- */
const TextField = ({
  id,
  label,
  required,
  value,
  onChange,
  onBlur,
  placeholder = "",
  type = "text",
  error,
  helperText,
  testid,
  maxLength
}) => {
  usePartnerTranslation();
  return <div className="space-y-1.5">
    <div className="relative">
      <label htmlFor={id} className="humrahe-field-label">
        {label}
        {required && <span className="text-[#E5484D]">*</span>}
      </label>
      <input id={id} name={id} type={type} value={value} onChange={onChange} onBlur={onBlur} placeholder={placeholder} maxLength={maxLength} data-testid={testid} aria-invalid={!!error} className={`humrahe-field ${error ? "humrahe-field-error" : ""}`} />
    </div>
    {error ? <p data-testid={`${testid}-error`} className="flex items-center gap-1.5 ps-1 text-xs font-medium text-[#E5484D]">
        <AlertCircle className="h-3.5 w-3.5" />
        {error}
      </p> : helperText ? <p className="ps-1 text-xs text-[#6B7280]">{helperText}</p> : null}
  </div>;
};

const SelectField = ({
  id,
  label,
  required,
  value,
  onChange,
  options,
  placeholder,
  error,
  testid
}) => {
  usePartnerTranslation();
  return <div className="space-y-1.5">
    <div className="relative">
      <label htmlFor={id} className="humrahe-field-label z-10">
        {label}
        {required && <span className="text-[#E5484D]">*</span>}
      </label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={testid} className={["humrahe-field !h-[52px] w-full justify-between !pe-10 text-start", error ? "humrahe-field-error" : "", !value ? "text-[#B6BAC2]" : "text-[#0F1115]"].join(" ")}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map(o => <SelectItem key={o.value} value={o.value} data-testid={`${testid}-option-${o.value}`}>
              {o.label}
            </SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    {error && <p data-testid={`${testid}-error`} className="flex items-center gap-1.5 ps-1 text-xs font-medium text-[#E5484D]">
        <AlertCircle className="h-3.5 w-3.5" />
        {error}
      </p>}
  </div>;
};

const PasswordField = ({
  id,
  label,
  required,
  value,
  onChange,
  onBlur,
  visible,
  onToggle,
  error,
  helperText,
  testid
}) => {
  usePartnerTranslation();
  return <div className="space-y-1.5">
    <div className="relative">
      <label htmlFor={id} className="humrahe-field-label">
        {label}
        {required && <span className="text-[#E5484D]">*</span>}
      </label>
      <input id={id} name={id} type={visible ? "text" : "password"} value={value} onChange={onChange} onBlur={onBlur} data-testid={testid} autoComplete="new-password" aria-invalid={!!error} className={`humrahe-field ${error ? "humrahe-field-error" : ""}`} />
      <button type="button" onClick={onToggle} data-testid={`${testid}-toggle`} aria-label={visible ? tr("Hide password") : tr("Show password")} className="absolute end-4 top-1/2 -translate-y-1/2 text-[#6B7280] transition-colors hover:text-[#0F1115]">
        {visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </button>
    </div>
    {error ? <p data-testid={`${testid}-error`} className="flex items-center gap-1.5 ps-1 text-xs font-medium text-[#E5484D]">
        <AlertCircle className="h-3.5 w-3.5" />
        {error}
      </p> : helperText ? <p className="ps-1 text-xs text-[#6B7280]">{helperText}</p> : null}
  </div>;
};

/* --------------------------- Main Page --------------------------- */
export const SignupPage = () => {
  usePartnerTranslation();
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState("");
  const [businessTypes, setBusinessTypes] = useState(BUSINESS_TYPES);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/supplier-registration-types`)
      .then((res) => {
        const items = res.data?.items || [];
        if (!cancelled && items.length > 0) {
          setBusinessTypes(
            items.map((item) => ({
              id: item.code,
              label: item.label
                .trim()
                .replace(/^As an?\s+/i, "")
                .replace(/^./, (letter) => letter.toUpperCase()),
              description: item.description,
              icon: item.icon,
            })),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setBusinessTypes(BUSINESS_TYPES);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main data-testid="signup-page" className="flex flex-1 items-start justify-center">
      {step === 1 ? (
        <BusinessTypeStep
          selected={selected}
          setSelected={setSelected}
          businessTypes={businessTypes}
          onNext={() => {
            if (selected) {
              setStep(2);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        />
      ) : (
        <BusinessDetailsStep
          businessType={selected}
          onBack={() => {
            setStep(1);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}
    </main>
  );
};

export default SignupPage;
